"use client";

import { useRef, useEffect, useState } from "react";
import Slider, { Settings } from "react-slick";
import Image from "next/image";
import arrowLeft from "../../lib/carousel/arrow_left.png";
import arrowRight from "../../lib/carousel/arrow_right.png";
import VideoEmbed from "./VideoEmbed";
const styles = new Proxy({"VideoCarousel":"VideoCarousel-c3a7a","Heading":"Heading-c3a7a","Title":"Title-c3a7a","Subtitle":"Subtitle-c3a7a","CarouselWrap":"CarouselWrap-c3a7a","SlideOuter":"SlideOuter-c3a7a","Card":"Card-c3a7a","VideoWrapper":"VideoWrapper-c3a7a","Unsupported":"Unsupported-c3a7a","CardBody":"CardBody-c3a7a","CardTitle":"CardTitle-c3a7a","CardExcerpt":"CardExcerpt-c3a7a","SliderWrap":"SliderWrap-c3a7a","Dots":"Dots-c3a7a","Dot":"Dot-c3a7a","DotActive":"DotActive-c3a7a","ArrowBtn":"ArrowBtn-c3a7a","ArrowPrev":"ArrowPrev-c3a7a","ArrowNext":"ArrowNext-c3a7a","CTA":"CTA-c3a7a"}, { get: (m, k) => (typeof k === "string" ? (m[k] ?? k) : m[k]) });
/*import "./VideoCarouselSlider.scss";*/
import { Video } from "./types";

// Same layout as BlogTeaser: three cards from lg up, a single one below.
const MOBILE_BREAKPOINT = 992;
const SLIDES_DESKTOP = 3;
const SLIDES_MOBILE = 1;

export default function VideoCarouselComponent({
  block,
}: {
  block: VideoCarouselComponentProps;
}) {
  const { videos, title, subtitle, cta, anchor, style } = block;
  const sliderRef = useRef<Slider>(null);
  const [slidesToShow, setSlidesToShow] = useState(SLIDES_DESKTOP);
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    const check = () =>
      setSlidesToShow(
        window.innerWidth < MOBILE_BREAKPOINT ? SLIDES_MOBILE : SLIDES_DESKTOP,
      );
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  // Only page horizontally when there are more videos than fit on screen.
  const pageable = videos.length > slidesToShow;
  // Desktop slides are hidden via opacity instead of overflow clipping (see
  // VideoCarouselSlider.scss), so it runs without clones to avoid a crossfade
  // flicker on wrap-around; mobile loops like the BlogTeaser carousel.
  const infinite = pageable && slidesToShow === SLIDES_MOBILE;
  const pages = infinite ? videos.length : videos.length - slidesToShow + 1;

  const SETTINGS: Settings = {
    dots: false,
    arrows: false,
    slidesToShow,
    slidesToScroll: 1,
    infinite,
    swipeToSlide: true,
    speed: 300,
    cssEase: "ease",
    afterChange: (index) => setCurrent(index),
  };

  return (
    <section
      id={anchor || "VideoCarousel"}
      className={styles.VideoCarousel}
      style={style}
    >
      <div className="container">
        <div className={styles.Heading}>
          <h2 className={styles.Title}>{title}</h2>
          {subtitle && <p className={styles.Subtitle}>{subtitle}</p>}
        </div>

        <div className={styles.CarouselWrap}>
          <div className={styles.SliderWrap}>
            <Slider
              ref={sliderRef}
              {...SETTINGS}
              className="VideoCarouselSlider"
            >
              {videos.map((video, i) => (
                <div key={i} className={styles.SlideOuter}>
                  <div className={styles.Card}>
                    <VideoEmbed
                      videoUrl={video.videoUrl}
                      videoTitle={video.videoTitle}
                    />
                    {(video.videoTitle || video.excerpt) && (
                      <div className={styles.CardBody}>
                        {video.videoTitle && (
                          <h3 className={styles.CardTitle}>
                            {video.videoTitle}
                          </h3>
                        )}
                        {video.excerpt && (
                          <p className={styles.CardExcerpt}>{video.excerpt}</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </Slider>
            {pageable && (
              <>
                <button
                  className={`${styles.ArrowBtn} ${styles.ArrowPrev}`}
                  onClick={() => sliderRef.current?.slickPrev()}
                  aria-label="Previous"
                >
                  <Image
                    src={arrowLeft}
                    alt="Previous"
                    width={20}
                    height={29}
                  />
                </button>
                <button
                  className={`${styles.ArrowBtn} ${styles.ArrowNext}`}
                  onClick={() => sliderRef.current?.slickNext()}
                  aria-label="Next"
                >
                  <Image src={arrowRight} alt="Next" width={20} height={29} />
                </button>
              </>
            )}
          </div>
          {pageable && (
            <ul className={styles.Dots}>
              {Array.from({ length: pages }, (_, i) => (
                <li
                  key={i}
                  className={
                    i === current % pages ? styles.DotActive : styles.Dot
                  }
                  onClick={() => sliderRef.current?.slickGoTo(i)}
                />
              ))}
            </ul>
          )}
        </div>

        {cta?.url && cta?.label && (
          <div className="row text-center mt-4">
            <div className="col-12">
              <a
                href={cta.url}
                className={styles.CTA}
                target="_blank"
                rel="noopener noreferrer"
              >
                {cta.label}
              </a>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

type VideoCarouselComponentProps = {
  videos: Video[];
  title: string;
  subtitle?: string;
  cta?: { label?: string; url?: string };
  anchor?: string;
  style?: React.CSSProperties;
};
